import React from "react";
import { useVisit } from "../../../contexts/VisitContext";
import { Subheading } from "../../../design/heading";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../design/table";
import VisitEventCell from "./VisitEventCell";
import DownloadFileButton from "../../../components/DownloadFileButton";
import RegeneratePDFButton from "./RegeneratePDFButton";
import { Text } from "../../../design/text";
import {
  CalendarIcon,
  ClipboardDocumentListIcon,
} from "@heroicons/react/24/outline";
import { Badge } from "../../../design/badge";
import { useAuth } from "../../../contexts/AuthContext";

interface VisitDetailEventListProps {
  visitId: string;
  eventReportPath?: string;
  visitStatus?: string;
}

const VisitDetailEventList: React.FC<VisitDetailEventListProps> = ({
  visitId,
  eventReportPath,
  visitStatus,
}) => {
  const { events } = useVisit();
  const { isAdmin, isMonitor } = useAuth();
  const canRegenerate = (isAdmin || isMonitor) && visitStatus === "completed";

  return (
    <>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Subheading>Events</Subheading>
          <Badge color="zinc">{events.length}</Badge>
        </div>
        <div className="flex items-center gap-2">
          {canRegenerate && <RegeneratePDFButton visitId={visitId} />}
          {eventReportPath && <DownloadFileButton path={eventReportPath} label="Download Notes" />}
        </div>
      </div>

      {/* Empty State */}
      {events.length === 0 && (
        <div className="mt-8 flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-zinc-200 py-12 dark:border-zinc-700">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-zinc-100 dark:bg-zinc-800">
            <ClipboardDocumentListIcon className="h-6 w-6 text-zinc-400 dark:text-zinc-500" />
          </div>
          <Text className="mt-4 font-medium text-zinc-900 dark:text-zinc-100">
            No events recorded
          </Text>
          <Text className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
            Events will appear here once the visit begins.
          </Text>
        </div>
      )}

      {/* Events Table */}
      {events.length > 0 && (
        <Table className="mt-4 [--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
          <TableHead>
            <TableRow>
              <TableHeader>
                <span className="flex items-center gap-1.5">
                  <CalendarIcon className="h-4 w-4" />
                  Date & Time
                </span>
              </TableHeader>
              <TableHeader>Type</TableHeader>
              <TableHeader>Details</TableHeader>
              <TableHeader>Media</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.map((event) => (
              <VisitEventCell key={event.id} event={event} />
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );
};

export default VisitDetailEventList;
